import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { CmsComponentData, CurrentProductService } from '@spartacus/storefront';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { CustomCarousel } from '../../simple-responsive-carousel/carousel.model';
import {
  Product,
  ProductScope,
  ProductService,
  isNotNullable,
  CmsProductCarouselComponent as model,
} from '@spartacus/core';
import { mediaConfig } from '../../../../default-media.config';
import { DEVICE_SIZE } from '../../../../../../../core/models/others.model';
import { ToolsService } from '../../../../../../../core/services/tools.service';
import { failStatus, initialStatus, successStatus } from '../../../../../../../core/models/request-status.model';
import { ProductAuxService } from '../../../../../product/_core/product-aux.service';
import { isEmpty } from '../../../../../../../core/services/simple-tools';

@Component({
  selector: 'cx-product-carousel',
  templateUrl: './product-carousel.component.html',
  styleUrl: './product-carousel.component.scss',
  standalone: false,
})
export class CustomProductCarouselComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClass = '';
  @HostBinding('attr.id') elID: string = new Date().toString().replaceAll(' ', '-');

  protected readonly PRODUCT_SCOPE = [ProductScope.LIST, ProductScope.STOCK];
  protected readonly PRODUCT_SCOPE_ITEM = [ProductScope.LIST_ITEM];

  private componentData$: Observable<model> = this.componentData.data$.pipe(filter(data => Boolean(data)));
  title$: Observable<string | undefined> = this.componentData$.pipe(map(data => data.title));
  items$ = new BehaviorSubject<Observable<Product | undefined>[]>([]);
  autoplayInterval = 5000;
  showCarousel$ = new BehaviorSubject<boolean>(false);

  corrouselConfig$ = new BehaviorSubject<CustomCarousel>({
    value: [],
    responsiveOptions: [
      {
        size: DEVICE_SIZE.WIDESCREEN,
        breakpoint: mediaConfig.mediaFormats!['widescreen'].width?.toString() + 'px',
        numVisible: 4,
        numScroll: 4,
      },
      {
        size: DEVICE_SIZE.DESKTOP,
        breakpoint: mediaConfig.mediaFormats!['desktop'].width?.toString() + 'px',
        numVisible: 3,
        numScroll: 3,
      },
      {
        size: DEVICE_SIZE.TABLET,
        breakpoint: mediaConfig.mediaFormats!['tablet'].width?.toString() + 'px',
        numVisible: 2,
        numScroll: 2,
      },
      {
        size: DEVICE_SIZE.MOBILE,
        breakpoint: mediaConfig.mediaFormats!['mobile'].width?.toString() + 'px',
        numVisible: 1,
        numScroll: 1,
      },
    ],
  });

  subscription = new Subscription();

  constructor(
    private componentData: CmsComponentData<any>,
    private productService: ProductService,
    private toolsService: ToolsService,
    private productAuxService: ProductAuxService,
    protected currentProductService: CurrentProductService,
  ) {}

  ngOnInit(): void {
    this.loadCarousel();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadCarousel() {
    this.subscription.add(
      this.getProduct()
        .pipe(distinctUntilKeyChanged<any>('code'))
        .subscribe(() => {
          this.resetCarousel();
          this.getComponent();
          this.checkProducts();
          this.getItems();
        }),
    );
  }

  resetCarousel() {
    this.items$.next([]);
    this.showCarousel$.next(false);
  }

  getProduct(): Observable<Product | null> {
    return this.currentProductService.getProduct([ProductScope.DETAILS, ProductScope.PRICE]);
  }

  getItems() {
    this.subscription.add(
      this.componentData$
        .pipe(
          take(1),
          map(data => {
            const _data: any = data;
            const config = this.corrouselConfig$.getValue();
            const componentMappingExist = !!data.composition?.inner?.length;
            const codes = data.productCodes?.trim().split(' ') ?? [];
            this.autoplayInterval = _data?.rotateInterval || 5000;
            config.autoplayInterval = this.autoplayInterval;

            this.setNewConfig(config);
            return { componentMappingExist, codes };
          }),
          map(({ componentMappingExist, codes }) => {
            const productScope = componentMappingExist ? [...this.PRODUCT_SCOPE] : [...this.PRODUCT_SCOPE_ITEM];
            return codes.map(code => this.productService.get(code, productScope));
          }),
          switchMap(items => combineLatest(items)),
          map(items => items.filter(item => isNotNullable(item))),
          filter(items => items.length > 0),
        )
        .subscribe(products => {
          this.setProducts(products);
        }),
    );
  }

  setProducts(products: Product[]) {
    const curretDevice = this.toolsService.getCurrentSize();
    const qtyTotal = this.qtyItemsVisible(curretDevice);

    if (products.length < qtyTotal) {
      const numberOfPlaceholders = qtyTotal - products.length;
      const placeholders = Array(numberOfPlaceholders).fill(undefined);
      products.push(...placeholders);
    }

    products = products.map(product => {
      if (!isEmpty(product?.code)) {
        return {
          ...product,
          thirdPartyPriceStatus: initialStatus,
          yourPrice: undefined,
          differencePrice: undefined,
        };
      }

      return product;
    });

    this.items$.next(products.map(product => of(product)));
    this.getPrices(products);
  }

  getPrices(allProducts: Product[]) {
    const productsIDS = allProducts
      .filter(product => product?.code)
      .filter(product => !isEmpty(product))
      .map(product => product.code) as string[];

    if (productsIDS.length) {
      this.subscription.add(
        this.productAuxService
          .getProductsPrices({ productsIDS })
          .pipe(take(1))
          .subscribe(productPrices => {
            allProducts.forEach(product => {
              if (!isEmpty(product?.code)) {
                const productPrice = productPrices.products?.find(_product => _product.productCode === product.code);

                if (productPrice) {
                  product.thirdPartyPriceStatus = successStatus;
                  product.yourPrice = productPrice.yourPrice;
                  product.differencePrice = productPrice.difference;
                } else {
                  product.thirdPartyPriceStatus = failStatus;
                }
              }
            });

            this.items$.next(allProducts.map(product => of(product)));
          }),
      );
    }
  }

  qtyItemsVisible(currentDevice?: DEVICE_SIZE): number {
    currentDevice = currentDevice ?? this.toolsService.getCurrentSize();
    const responsiveOptions = this.corrouselConfig$.getValue().responsiveOptions;
    return responsiveOptions?.find(config => config.size === currentDevice).numVisible;
  }

  getComponent() {
    this.subscription.add(
      this.componentData.data$.subscribe(component => {
        this.elID = component.uid ? component.uid : this.elID;
      }),
    );
  }

  checkProducts() {
    this.subscription.add(
      this.items$.pipe(filter(items => items.length > 0)).subscribe(items => {
        this.configCarousel(items.length);
      }),
    );
  }

  getCurrentResponsiveConfig() {
    const curretDevice = this.toolsService.getCurrentSize();
    const responsiveOptions = this.corrouselConfig$.getValue().responsiveOptions;
    const curretnConfig = responsiveOptions?.find(config => config.size === curretDevice);
    return curretnConfig;
  }

  configCarousel(qtyItems: number) {
    let config = this.corrouselConfig$.getValue();
    this.hostClass = qtyItems === 0 ? 'd-none' : '';

    config = {
      ...config,
      ...{
        showNavigators: qtyItems > this.getCurrentResponsiveConfig().numVisible,
        showIndicators: qtyItems > this.getCurrentResponsiveConfig().numVisible,
        autoplayInterval: qtyItems > this.getCurrentResponsiveConfig().numVisible ? this.autoplayInterval : 0,
      },
    };

    this.setNewConfig(config);
  }

  convertItemToProduct(item: any): Product {
    return item;
  }

  setNewConfig(config: CustomCarousel) {
    this.showCarousel$.next(false);
    this.corrouselConfig$.next(config);

    setTimeout(() => {
      this.showCarousel$.next(true);
    }, 500);
  }
}
